import React, { useState } from 'react'
import axios from 'axios'
import {
  TextField,
  Popper,
  Divider,
  Paper,
  Button
} from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useDispatch, useSelector } from 'react-redux'
import { checkErrorStatus, setSnackbar } from 'utils/common/actions'
import useStyles from './styles'
import { ReactComponent as SearchIcon } from 'static/icons/search.svg'
import { ReactComponent as AdviceIcon } from 'static/icons/advice.svg'
import { ReactComponent as LegalChangeIcon } from 'static/icons/legal-change.svg'
import { ReactComponent as CreateIcon } from 'static/icons/add-r.svg'
import { userIsViewer } from 'helpers/dataHelpers'
import history from 'browserHistory'
import Config from 'config'
import useDebouncedSearch, { SEARCH_MIN_CHARS } from 'helpers/useDebouncedSearch'


const SearchRequestAdvice = (props) => {
  const {
    additionalRootClasses,
    additionalClasses,
    isAdmin,
    disabled
  } = props
  const localClasses = useStyles()
  const { apiUrl } = Config.network
  const dispatch = useDispatch()
  const [inputValue, setInputValue] = useState('')
  const [popperOpen, setPopperOpen] = useState(false)
  const [showTextField, setShowTextField] = useState(false)
  const userType = useSelector(state => state.auth.userType)

  const handleRequestError = (error) => {
    if (error.response) {
      dispatch(checkErrorStatus(error.response.status))
    } else {
      dispatch(setSnackbar('Connection error', 'error'))
    }
    return []
  }

  const searchAdvicesAndLegalChanges = (search, cancelToken) => {
    const params = { 'getAll': true, search }
    return axios.get(`${apiUrl}search/simple-list/`, { params, cancelToken }).then(({ data }) => {
      const advicesList = (data.advices || []).map(item => ({ ...item, type: 'advice' }))
      const legalChangesList = (data.legal_changes || []).map(item => ({ ...item, type: 'legalChange' }))
      return [...advicesList, ...legalChangesList]
    }).catch(error => {
      if (axios.isCancel(error)) {
        throw error
      }
      return handleRequestError(error)
    })
  }

  const { results: advices, isLoading, searchNow } = useDebouncedSearch(
    inputValue,
    searchAdvicesAndLegalChanges,
    { enabled: showTextField }
  )

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      searchNow()
    }
  }

  const getNoOptionsText = () => (
    inputValue.trim().length < SEARCH_MIN_CHARS ?
      `Type at least ${SEARCH_MIN_CHARS} characters to search...` :
      'No results found'
  )


  const handleActionClick = (event) => {
    history.push(`/requests/add?name=${inputValue}`)
  }

  const handleButtonClick = () => {
    setShowTextField(true)
    setPopperOpen(true)
  }

  const handlePopperClose = () => {
    setPopperOpen(false)
    setShowTextField(false)
    setInputValue('')
  }

  const getActionButton = () => {
    if (userIsViewer(userType)) {
      return null
    }
    if (isAdmin) {
      return (
        <>
          <Button
            disabled={disabled}
            className={localClasses.popperAction}
            color='primary'
            onMouseDown={() => history.push(`/advices/add?title=${inputValue}`)}
          >
            Create an Advice
          </Button>
          <Button
            disabled={disabled}
            className={localClasses.popperAction}
            color='primary'
            onMouseDown={handleActionClick}
          >
            Request an Advice
          </Button>
        </>)
    } else {
      return (
        <Button
          disabled={disabled}
          className={localClasses.popperAction}
          color='primary'
          onMouseDown={handleActionClick}
        >
          Request an Advice
        </Button>)
    }
  }

  const getActionText = () => {
    if (userIsViewer(userType)) {
      return 'Search Advice'
    }
    if (isAdmin) {
      return 'Search or Create advice'
    } else {
      return 'Request or Search Advice'
    }
  }

  const CusomPopper = function (props) {
    return (
      <Popper {...props} className={localClasses.popper} placement='bottom-start'>
        {props.children}
        {
          !userIsViewer(userType) ?
          <>
            <Divider className={localClasses.popperDivider} />
            <div className={localClasses.popperActionContainer}>
            {
              getActionButton()
            }
            </div>
          </> : null
        }
      </Popper>
  )}

  const CustomPaper = function (props) {
    return (
     <Paper {...props} className={localClasses.paper} elevation={0} />
  )}

  const handleChange = (event, newValue) => {
    setInputValue('')
    history.push(newValue.type === 'legalChange' ?
      `/legal-changes/${newValue.id}` : `/advices/${newValue.id}`)
  }

  const handleInput = (event, newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue)
    }
  }
  return (
    <>
      <Button
        disabled={disabled}
        variant='contained'
        color='primary'
        onClick={handleButtonClick}
        className={localClasses.newAdviceButton}
        endIcon={isAdmin ? <CreateIcon className={localClasses.buttonIcon} /> : <SearchIcon className={localClasses.buttonIcon} />}
        disableElevation
      >
        {
          getActionText()
        }
      </Button>
      {
        showTextField &&
        <Autocomplete
          openOnFocus={true}
          filterOptions={() => {return advices}}
          loading={isLoading}
          noOptionsText={getNoOptionsText()}
          clearOnEscape={true}
          onOpen={() => setPopperOpen(true)}
          onClose={handlePopperClose}
          PopperComponent={CusomPopper}
          PaperComponent={CustomPaper}
          onChange={handleChange}
          onInputChange={handleInput}
          options={advices}
          getOptionSelected={(option, value) => option.type === value.type && option.id === value.id}
          disableClearable
          getOptionLabel={(option) => option.title}
          inputValue={inputValue}
          renderOption={(option) => (
            <div className={localClasses.option}>
              {
                option.type === 'legalChange' ?
                <LegalChangeIcon className={localClasses.optionIcon} /> :
                <AdviceIcon className={localClasses.optionIcon} />
              }
              <span>{option.title}</span>
            </div>
          )}
          popupIcon={
            <SearchIcon className={localClasses.searchIcon} />
          }
          className={`${additionalClasses} ${localClasses.autocompleteRoot}`}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              onKeyDown={handleSearchKeyDown}
              placeholder={`${getActionText()}...`}
              margin='normal'
              variant='outlined'
              InputProps={{
                ...params.InputProps,
                classes: {
                  root: `${localClasses.searchRoot} ${additionalRootClasses || ''} ${popperOpen && localClasses.searchRootOpen}`,
                  input: localClasses.searchInput,
                  notchedOutline: localClasses.searchNotchedOutline
                },
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? <CircularProgress color='primary' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />
      }
    </>
  )
}

export default SearchRequestAdvice
